document.addEventListener('DOMContentLoaded', () => {
  // Check of GSAP beschikbaar is
  const useGSAP = typeof gsap !== 'undefined';

  // Selecteer alle elementen die geanimeerd moeten worden
  const elementsToAnimate = document.querySelectorAll('p, h1, h2, h3, img');

  if (elementsToAnimate.length === 0) {
    return;
  }

  // Bepaal de richting waarin een element de viewport binnenkomt
  const getEntryDirection = (entry, element, isExiting = false) => {
    const rect = entry.boundingClientRect;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    const elementTop = rect.top;
    const elementBottom = rect.bottom;
    const elementLeft = rect.left;
    const elementRight = rect.right;

    const distanceFromTop = elementTop;
    const distanceFromBottom = viewportHeight - elementBottom;
    const distanceFromLeft = elementLeft;
    const distanceFromRight = viewportWidth - elementRight;

    // Bepaal welke kant het dichtstbij is
    const minVertical = Math.min(distanceFromTop, distanceFromBottom);
    const minHorizontal = Math.min(distanceFromLeft, distanceFromRight);

    let direction;

    if (isExiting) {
      // Bij uitgaan: bepaal richting op basis van waar element naartoe gaat
      // Als element boven viewport is → gaat naar boven
      // Als element onder viewport is → gaat naar onderen
      if (elementBottom < 0) {
        direction = 'top'; // Element gaat naar boven
      } else if (elementTop > viewportHeight) {
        direction = 'bottom'; // Element gaat naar onderen
      } else if (elementRight < 0) {
        direction = 'left'; // Element gaat naar links
      } else if (elementLeft > viewportWidth) {
        direction = 'right'; // Element gaat naar rechts
      } else {
        // Fallback: gebruik normale logica
        if (minVertical < minHorizontal) {
          direction = distanceFromTop < distanceFromBottom ? 'top' : 'bottom';
        } else {
          direction = distanceFromLeft < distanceFromRight ? 'left' : 'right';
        }
      }
    } else {
      // Bij binnenkomen: bepaal richting op basis van waar element vandaan komt
      if (minVertical < minHorizontal) {
        // Element is dichter bij top of bottom rand
        if (distanceFromTop < distanceFromBottom) {
          direction = 'top'; // Element komt van boven
        } else {
          direction = 'bottom'; // Element komt van onderen
        }
      } else {
        // Element is dichter bij linker of rechter rand
        if (distanceFromLeft < distanceFromRight) {
          direction = 'left'; // Element komt van links
        } else {
          direction = 'right'; // Element komt van rechts
        }
      }
    }

    return direction;
  };

  // Bepaal de transform waarde op basis van richting
  const getInitialTransform = (direction) => {
    const offset = 32; // 32px offset
    switch (direction) {
      case 'top':
        return `translateY(-${offset}px)`;
      case 'bottom':
        return `translateY(${offset}px)`;
      case 'left':
        return `translateX(-${offset}px)`;
      case 'right':
        return `translateX(${offset}px)`;
      default:
        return `translateY(${offset}px)`;
    }
  };

  // Pas de initiële state toe met CSS (zonder opacity)
  const applyInitialState = (element, direction) => {
    const transform = getInitialTransform(direction);

    // Sla originele transform op als dat nog niet gedaan is
    if (!element.dataset.originalTransform) {
      const computedStyle = window.getComputedStyle(element);
      element.dataset.originalTransform = computedStyle.transform || 'none';
      element.dataset.originalTransition = computedStyle.transition || '';
    }

    // Force reflow door element te lezen
    void element.offsetHeight;

    // Pas initiële state toe - ZONDER transition eerst
    element.style.transition = 'none';
    element.style.transform = transform;
    element.style.willChange = 'transform';

    // Force reflow om ervoor te zorgen dat de browser de initial state ziet
    void element.offsetHeight;

    // Nu pas de transition toevoegen (alleen transform, geen opacity)
    element.style.transition = 'transform 0.7s ease-out';
  };

  // Animateer het element in (zonder opacity)
  const animateIn = (element) => {
    if (useGSAP) {
      // Gebruik GSAP als beschikbaar (alleen transform, geen opacity)
      gsap.to(element, {
        x: 0,
        y: 0,
        duration: 0.7,
        ease: 'power2.out',
        onComplete: () => {
          element.style.willChange = 'auto';
        }
      });
    } else {
      // Gebruik CSS transitions (alleen transform, geen opacity)
      // Force reflow
      void element.offsetHeight;

      // Pas target state toe
      element.style.transform = 'translateX(0) translateY(0)';

      // Cleanup na animatie
      const handleTransitionEnd = (e) => {
        // Alleen als transform transition klaar is
        if (e.target === element && e.propertyName === 'transform') {
          element.removeEventListener('transitionend', handleTransitionEnd);
          element.style.willChange = 'auto';
        }
      };
      element.addEventListener('transitionend', handleTransitionEnd);

      // Fallback timeout voor het geval transitionend niet wordt getriggerd
      setTimeout(() => {
        element.style.willChange = 'auto';
      }, 800);
    }
  };

  // Reset element naar initial state wanneer het uit beeld gaat
  const resetToInitialState = (element, direction) => {
    const transform = getInitialTransform(direction);

    // Zet transition op none voor directe reset
    element.style.transition = 'none';
    element.style.transform = transform;

    // Force reflow
    void element.offsetHeight;

    // Herstel transition voor volgende animatie
    element.style.transition = 'transform 0.7s ease-out';
  };

  // IntersectionObserver om te detecteren wanneer elementen de viewport binnenkomen
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      const element = entry.target;
      const isIntersecting = entry.isIntersecting;

      if (isIntersecting) {
        // Element komt in beeld - bepaal richting en animeer
        const direction = getEntryDirection(entry, element);

        // Pas initial state toe
        applyInitialState(element, direction);

        // Start animatie met minimale delay
        setTimeout(() => {
          animateIn(element);
        }, 10); // Korte delay om initial state te laten settlen
      } else {
        // Element gaat uit beeld - reset naar initial state voor volgende keer
        const direction = getEntryDirection(entry, element, true); // true = isExiting
        resetToInitialState(element, direction);
      }
    });
  }, {
    threshold: 0.1,
    rootMargin: '0px 0px 20% 0px', // Start animatie eerder (20% voor element zichtbaar wordt)
  });

  // Check of element al (gedeeltelijk) zichtbaar is in viewport
  const isElementInViewport = (element) => {
    const rect = element.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;

    // Check of element gedeeltelijk zichtbaar is
    return (
      rect.top < viewportHeight &&
      rect.bottom > 0 &&
      rect.left < viewportWidth &&
      rect.right > 0
    );
  };

  // Observeer alle elementen
  elementsToAnimate.forEach((element) => {
    // Skip elementen die al een data-enter-viewport attribuut hebben (handmatige animatie)
    if (element.hasAttribute('data-enter-viewport')) {
      return;
    }

    // Skip elementen die al zichtbaar zijn bij het laden
    if (isElementInViewport(element)) {
      return;
    }

    observer.observe(element);
  });
});